import * as THREE from 'three';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateSunlightScore, SunlightScoreBreakdown } from '@/services/score/sunlight';
import { DailySunlightResult } from '@/types/sunlight';
import { highlightRoomFromData, removeHighlightFromData } from '@/utils/visualization';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { WorkflowProps } from '@/types/workflow';
import styles from './styles.module.scss';
import { handleAddScoreToRoomModal } from '../AddScoreToRoomModal/AddScoreToRoomModal';
import { FormaTab, FormaTabs } from '../UI/FormaTabs';
import { useWorkflowStore } from '@/store/workflowStore';
import { WeaveTooltip } from '../UI/WeaveTooltip/WeaveTooltip';
import { WeaveAccordion } from '../UI/WeaveAccordion/WeaveAccordion';
import { getBuildingGraph } from '@/utils/visualization';

interface SunlightListProps {
	data: Record<string, DailySunlightResult[]>;
	scene: THREE.Scene;
	selectedBuildingId: string | null;
	workflowProps?: WorkflowProps;
}

interface SunlightListForRegionsProps {
	data: Record<string, DailySunlightResult[]>[] | undefined;
	scene: THREE.Scene;
	selectedBuildingId: string | null;
	workflowProps?: WorkflowProps;
}

const formatRoomName = (roomName: string): string => {
	const cleaned = roomName.replace(/^RoomFloorFill_/, '');
	return cleaned.replace(/_/g, ' ');
};

const sortRoomEntries = (
	entries: [string, DailySunlightResult[]][]
): [string, DailySunlightResult[]][] => {
	return entries.sort(([nameA], [nameB]) => {
		const matchA = nameA.match(/Floor(\d+)_Space(\d+)/);
		const matchB = nameB.match(/Floor(\d+)_Space(\d+)/);

		if (!matchA || !matchB) return nameA.localeCompare(nameB);

		const [, floorA, spaceA] = matchA.map(Number);
		const [, floorB, spaceB] = matchB.map(Number);

		if (floorA !== floorB) return floorA - floorB;
		return spaceA - spaceB;
	});
};

const getFloorLabelFromRoomName = (roomName: string): string => {
	const match = roomName.match(/Floor(\d+)_/);
	if (!match) {
		return 'Unassigned Floor';
	}

	const floorNumber = Number(match[1]);
	if (!Number.isFinite(floorNumber)) {
		return 'Unassigned Floor';
	}

	const normalizedFloor = Math.max(0, floorNumber - 1);
	return `Floor ${normalizedFloor}`;
};

const getAverageZFromResults = (results: DailySunlightResult[]): number | null => {
	if (results.length === 0) {
		return null;
	}

	let zSum = 0;
	let count = 0;
	results.forEach((result) => {
		const z = result.point?.z;
		if (typeof z === 'number' && Number.isFinite(z)) {
			zSum += z;
			count += 1;
		}
	});

	if (count === 0) {
		return null;
	}

	return zSum / count;
};

const groupEntriesByFloor = (
	entries: Array<{ key: string; roomName: string; results: DailySunlightResult[] }>,
	resolveFloorIndex?: (z: number | null) => number | null
) => {
	const grouped = new Map<string, Array<{ key: string; roomName: string; results: DailySunlightResult[] }>>();
	const unresolvedByZ: Array<{ key: string; roomName: string; results: DailySunlightResult[]; avgZ: number | null }> = [];

	entries.forEach((entry) => {
		const floorLabel = getFloorLabelFromRoomName(entry.roomName);
		if (floorLabel === 'Unassigned Floor') {
			const avgZ = getAverageZFromResults(entry.results);
			if (resolveFloorIndex) {
				const floorIndex = resolveFloorIndex(avgZ);
				if (floorIndex !== null) {
					const resolvedLabel = `Floor ${floorIndex}`;
					const existingResolved = grouped.get(resolvedLabel);
					if (existingResolved) {
						existingResolved.push(entry);
					} else {
						grouped.set(resolvedLabel, [entry]);
					}
					return;
				}
			}

			unresolvedByZ.push({
				...entry,
				avgZ,
			});
			return;
		}

		const existing = grouped.get(floorLabel);
		if (existing) {
			existing.push(entry);
		} else {
			grouped.set(floorLabel, [entry]);
		}
	});

	if (unresolvedByZ.length > 0) {
		const withZ = unresolvedByZ
			.filter((entry) => entry.avgZ !== null)
			.sort((a, b) => (a.avgZ as number) - (b.avgZ as number));
		const withoutZ = unresolvedByZ.filter((entry) => entry.avgZ === null);

		const zClusters: Array<{ center: number; members: Array<{ key: string; roomName: string; results: DailySunlightResult[] }> }> = [];
		const Z_CLUSTER_TOLERANCE = 1.5;

		withZ.forEach((entry) => {
			const z = entry.avgZ as number;
			const cluster = zClusters.find((candidate) => Math.abs(candidate.center - z) <= Z_CLUSTER_TOLERANCE);
			if (cluster) {
				cluster.members.push({ key: entry.key, roomName: entry.roomName, results: entry.results });
				const size = cluster.members.length;
				cluster.center = (cluster.center * (size - 1) + z) / size;
				return;
			}

			zClusters.push({
				center: z,
				members: [{ key: entry.key, roomName: entry.roomName, results: entry.results }],
			});
		});

		zClusters.sort((a, b) => a.center - b.center);
		zClusters.forEach((cluster, index) => {
			grouped.set(`Floor ${index}`, cluster.members);
		});

		if (withoutZ.length > 0) {
			grouped.set(
				'Unassigned Floor',
				withoutZ.map((entry) => ({ key: entry.key, roomName: entry.roomName, results: entry.results }))
			);
		}
	}

	return Array.from(grouped.entries()).sort((a, b) => {
		if (a[0] === 'Unassigned Floor') return 1;
		if (b[0] === 'Unassigned Floor') return -1;
		const aNum = Number(a[0].replace('Floor ', ''));
		const bNum = Number(b[0].replace('Floor ', ''));
		return aNum - bNum;
	});
};

const useBuildingFloorResolver = (scene: THREE.Scene, selectedBuildingId: string | null) => {
	const [levelHeights, setLevelHeights] = useState<number[]>([]);
	const [buildingBaseZ, setBuildingBaseZ] = useState<number | null>(null);

	useEffect(() => {
		let cancelled = false;

		const loadLevels = async () => {
			if (!selectedBuildingId) {
				setLevelHeights([]);
				setBuildingBaseZ(null);
				return;
			}

			const building = scene.getObjectByProperty('uuid', selectedBuildingId);
			if (!building) {
				setLevelHeights([]);
				setBuildingBaseZ(null);
				return;
			}

			const bbox = new THREE.Box3().setFromObject(building);
			const baseZ = Number.isFinite(bbox.min.z) ? bbox.min.z : null;
			if (!cancelled) {
				setBuildingBaseZ(baseZ);
			}

			try {
				const graphBuilding = await getBuildingGraph(building);
				const heights = (graphBuilding as { data?: { levels?: Array<{ height?: number }> } } | undefined)
					?.data?.levels
					?.map((level) => Number(level.height))
					.filter((height) => Number.isFinite(height) && height > 0) as number[] | undefined;

				if (!cancelled) {
					setLevelHeights(heights ?? []);
				}
			} catch (error) {
				console.warn('Failed to load building graph for sunlight floor grouping:', error);
				if (!cancelled) {
					setLevelHeights([]);
				}
			}
		};

		loadLevels();

		return () => {
			cancelled = true;
		};
	}, [scene, selectedBuildingId]);

	return useCallback((z: number | null): number | null => {
		if (z === null || buildingBaseZ === null || levelHeights.length === 0) {
			return null;
		}

		let cumulativeZ = buildingBaseZ;
		for (let i = 0; i < levelHeights.length; i++) {
			cumulativeZ += levelHeights[i];
			if (z <= cumulativeZ) {
				return i;
			}
		}

		return levelHeights.length - 1;
	}, [buildingBaseZ, levelHeights]);
};

export const SunlightList = (props: SunlightListProps) => {
	const { scoreList, byRoomContent, scoreItemsContainer } = styles;
	const { data, scene, selectedBuildingId, workflowProps } = props;
	const sortedEntries = sortRoomEntries(Object.entries(data));
	const resolveFloorIndex = useBuildingFloorResolver(scene, selectedBuildingId);
	const groupedEntries = useMemo(
		() =>
			groupEntriesByFloor(
				sortedEntries.map(([roomName, results]) => ({
					key: roomName,
					roomName,
					results,
				})),
				resolveFloorIndex
			),
		[sortedEntries, resolveFloorIndex]
	);

	const handleAddToRoom = async (results: DailySunlightResult[]) => {
		if (workflowProps) {
			handleAddScoreToRoomModal(results, workflowProps);
		}
	};

	if (sortedEntries.length === 0) {
		return <div>No sunlight data available. Please run the sunlight analysis.</div>;
	}

	// Calculate aggregate statistics
	let totalScore = 0;
	let totalWinterScore = 0;
	let totalSummerScore = 0;
	let totalSpringFallScore = 0;
	let totalConsistencyScore = 0;
	let totalQualityScore = 0;
	let totalAverageHours = 0;
	let totalPoints = 0;
	const roomCount = sortedEntries.length;

	sortedEntries.forEach(([, results]) => {
		const score = calculateSunlightScore(results);
		totalScore += score.totalScore;
		totalWinterScore += score.winterScore;
		totalSummerScore += score.summerScore;
		totalSpringFallScore += score.springFallScore;
		totalConsistencyScore += score.consistencyScore;
		totalQualityScore += score.qualityScore;
		totalAverageHours += score.averageHours;
		totalPoints += score.pointsAnalyzed;
	});

	const avgScore = totalScore / roomCount;
	const avgWinterScore = totalWinterScore / roomCount;
	const avgSummerScore = totalSummerScore / roomCount;
	const avgSpringFallScore = totalSpringFallScore / roomCount;
	const avgConsistencyScore = totalConsistencyScore / roomCount;
	const avgQualityScore = totalQualityScore / roomCount;
	const avgHours = totalAverageHours / roomCount;

	return (
		<div>
			<FormaTabs gap={8} density="medium">
				<FormaTab for="aggregate" label="Overall Summary" />
				<div id="aggregate" slot="content">
					<div className={styles.aggregateView}>
						<div className={styles.legend}>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Average Total Score</span>
									<span className={styles.percentage}>{avgScore.toFixed(1)}/100</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Winter Score</span>
									<span className={styles.percentage}>{avgWinterScore.toFixed(1)}/30</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Summer Score</span>
									<span className={styles.percentage}>{avgSummerScore.toFixed(1)}/25</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Spring/Fall Score</span>
									<span className={styles.percentage}>{avgSpringFallScore.toFixed(1)}/25</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Consistency Score</span>
									<span className={styles.percentage}>{avgConsistencyScore.toFixed(1)}/10</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Quality Score</span>
									<span className={styles.percentage}>{avgQualityScore.toFixed(1)}/10</span>
								</div>
							</div>
						</div>
						<div className={styles.summary}>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Total points analyzed:</span>
								<span className={styles.summaryValue}>{totalPoints.toLocaleString()}</span>
							</div>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Rooms analyzed:</span>
								<span className={styles.summaryValue}>{roomCount}</span>
							</div>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Avg daily sunlight:</span>
								<span className={styles.summaryValue}>{avgHours.toFixed(1)} hrs/day</span>
							</div>
						</div>
					</div>
				</div>
				<FormaTab for="byRoom" label="By Room" />
				<div id="byRoom" slot="content" className={byRoomContent}>
					<div className={scoreList}>
						{groupedEntries.map(([floorLabel, floorEntries], floorIndex) => (
							<WeaveAccordion
								key={floorLabel}
								label={floorLabel}
								expanded={floorIndex === 0}
								indicatorposition="right"
							>
								<div className={scoreItemsContainer}>
									{floorEntries.map((entry) => (
										<div key={entry.key}>
											<ScoreItem
												scoreBreakdown={calculateSunlightScore(entry.results)}
												room={entry.roomName}
												handleMouseIn={() => highlightRoomFromData(entry.results, scene)}
												handleMouseOut={() => removeHighlightFromData(entry.results, scene)}
												handleAddToRoom={() => handleAddToRoom(entry.results)}
											/>
										</div>
									))}
								</div>
							</WeaveAccordion>
						))}
					</div>
				</div>
			</FormaTabs>
		</div>
	);
};

export const SunlightListForRegions = (props: SunlightListForRegionsProps) => {
	const { scoreList, scoreItemsContainer, byRoomContent } = styles;
	const { data, scene, selectedBuildingId, workflowProps } = props;
	const resolveFloorIndex = useBuildingFloorResolver(scene, selectedBuildingId);

	const handleAddToRoom = async (results: DailySunlightResult[]) => {
		if (workflowProps) {
			handleAddScoreToRoomModal(results, workflowProps);
		}
	};

	const allEntries = useMemo(
		() =>
			(data ?? []).flatMap((record, regionIndex) =>
				Object.entries(record).map(([roomName, results]) => ({
					key: `${regionIndex}-${roomName}`,
					roomName,
					results,
				}))
			),
		[data]
	);

	const groupedEntries = useMemo(
		() => groupEntriesByFloor(allEntries, resolveFloorIndex),
		[allEntries, resolveFloorIndex]
	);

	if (!data) {
		return null;
	}

	if (allEntries.length === 0) {
		return <div>No sunlight data available. Please run the sunlight analysis.</div>;
	}

	// Calculate aggregate statistics
	let totalScore = 0;
	let totalWinterScore = 0;
	let totalSummerScore = 0;
	let totalSpringFallScore = 0;
	let totalConsistencyScore = 0;
	let totalQualityScore = 0;
	let totalAverageHours = 0;
	let totalPoints = 0;
	const roomCount = allEntries.length;

	allEntries.forEach(({ results }) => {
		const score = calculateSunlightScore(results);
		totalScore += score.totalScore;
		totalWinterScore += score.winterScore;
		totalSummerScore += score.summerScore;
		totalSpringFallScore += score.springFallScore;
		totalConsistencyScore += score.consistencyScore;
		totalQualityScore += score.qualityScore;
		totalAverageHours += score.averageHours;
		totalPoints += score.pointsAnalyzed;
	});

	const avgScore = totalScore / roomCount;
	const avgWinterScore = totalWinterScore / roomCount;
	const avgSummerScore = totalSummerScore / roomCount;
	const avgSpringFallScore = totalSpringFallScore / roomCount;
	const avgConsistencyScore = totalConsistencyScore / roomCount;
	const avgQualityScore = totalQualityScore / roomCount;
	const avgHours = totalAverageHours / roomCount;

	return (
		<div>
			<FormaTabs gap={8} density="medium">
				<FormaTab for="aggregate" label="Overall Summary" />
				<div id="aggregate" slot="content">
					<div className={styles.aggregateView}>
						<div className={styles.legend}>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Average Total Score</span>
									<span className={styles.percentage}>{avgScore.toFixed(1)}/100</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Winter Score</span>
									<span className={styles.percentage}>{avgWinterScore.toFixed(1)}/30</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Summer Score</span>
									<span className={styles.percentage}>{avgSummerScore.toFixed(1)}/25</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Spring/Fall Score</span>
									<span className={styles.percentage}>{avgSpringFallScore.toFixed(1)}/25</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Consistency Score</span>
									<span className={styles.percentage}>{avgConsistencyScore.toFixed(1)}/10</span>
								</div>
							</div>
							<div className={styles.legendItem}>
								<div className={styles.legendRow}>
									<span className={styles.categoryName}>Quality Score</span>
									<span className={styles.percentage}>{avgQualityScore.toFixed(1)}/10</span>
								</div>
							</div>
						</div>
						<div className={styles.summary}>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Total points analyzed:</span>
								<span className={styles.summaryValue}>{totalPoints.toLocaleString()}</span>
							</div>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Rooms analyzed:</span>
								<span className={styles.summaryValue}>{roomCount}</span>
							</div>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Avg daily sunlight:</span>
								<span className={styles.summaryValue}>{avgHours.toFixed(1)} hrs/day</span>
							</div>
						</div>
					</div>
				</div>
				<FormaTab for="byRoom" label="By Room" />
				<div id="byRoom" slot="content" className={byRoomContent}>
					<div className={scoreList}>
						{groupedEntries.map(([floorLabel, floorEntries], floorIndex) => (
							<WeaveAccordion
								key={floorLabel}
								label={floorLabel}
								expanded={floorIndex === 0}
								indicatorposition="right"
							>
								<div className={scoreItemsContainer}>
									{floorEntries.map((entry) => (
										<div key={entry.key}>
											<ScoreItem
												scoreBreakdown={calculateSunlightScore(entry.results)}
												room={entry.roomName}
												handleMouseIn={() => highlightRoomFromData(entry.results, scene, true)}
												handleMouseOut={() => removeHighlightFromData(entry.results, scene)}
												handleAddToRoom={() => handleAddToRoom(entry.results)}
											/>
										</div>
									))}
								</div>
							</WeaveAccordion>
						))}
					</div>
				</div>
			</FormaTabs>
		</div>
	);
};

const ScoreItem = (props: {
	scoreBreakdown: SunlightScoreBreakdown;
	room: string;
	handleMouseIn: () => void;
	handleMouseOut: () => void;
	handleAddToRoom: () => void;
}) => {
	const { scoreBreakdown, room, handleMouseIn, handleMouseOut, handleAddToRoom } = props;
	const currentStage = useWorkflowStore((state) => state.currentStage);
	const formattedRoom = formatRoomName(room);

	const formatedBreakdown = {
		Winter: `${scoreBreakdown.winterScore.toFixed(1)}/30`,
		Summer: `${scoreBreakdown.summerScore.toFixed(1)}/25`,
		'Spring/Fall': `${scoreBreakdown.springFallScore.toFixed(1)}/25`,
		Consistency: `${scoreBreakdown.consistencyScore.toFixed(1)}/10`,
		Quality: `${scoreBreakdown.qualityScore.toFixed(1)}/10`,
		'Avg Hours': `${scoreBreakdown.averageHours.toFixed(1)} hrs/day`,
	}

	return (
		<div
			onMouseEnter={handleMouseIn}
			onMouseLeave={handleMouseOut}
			className={styles.scoreItem}
		>
			<div className={styles.scoreHeader}>
				<div className={styles.scoreTitle}>
					<span className={styles.scoreNumber}>
						{formattedRoom}
					</span>
				</div>
				<div className={styles.scoreMeta}>
					<span className={styles.scoreValue}>
						Score: {scoreBreakdown.totalScore.toFixed(2)}
					</span>
				</div>
			</div>
			<div className={styles.scoreStats}>
				{Object.entries(formatedBreakdown).map(([key, value]) => (
					<div key={key} className={styles.scoreStatItem}>
						<div className={styles.scoreStatRow}>
							<span className={styles.scoreStatLabel}>{key}</span>
							<span className={styles.scoreStatValue}>{value}</span>
						</div>
					</div>
				))}
			</div>
			<WeaveTooltip
				className={styles.addToARoomButton}
				text={currentStage !== 'design_development'
					? 'Available in Gate 3'
					: 'Add this room\'s sunlight score to the room'
				}>
				<WeaveButton
					onClick={handleAddToRoom}
					disabled={currentStage !== 'design_development'}
				>
					Add to Room
				</WeaveButton>
			</WeaveTooltip>
		</div>
	);
};
